import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { BehaviorSubject, filter, forkJoin, map, Observable, shareReplay, take } from 'rxjs';
import { ResponseDto, CieSubjectGrades, CieRankScoreCalc, StudentSavedGrades } from 'src/app/domain';
import { CieSubject, CieGrade, CalculationRequestSubjects, CieChosenSubjectGrade } from 'src/app/domain/cie-subject.model';
import { environment } from 'src/environments/environment';
import { AppStorageService } from './app-storage.service';
import { UserService } from './user.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class CieService {
  private readonly http = inject(HttpClient);
  private readonly storageService = inject(AppStorageService);
  private readonly userService = inject(UserService);

  private readonly cieAllSubjects = new BehaviorSubject<CieSubject[]>([]);
  cieAllSubjects$: Observable<CieSubject[]>;

  private readonly calculatedTotalScore = new BehaviorSubject<string>('');
  calculatedTotalScore$: Observable<string>;

  private readonly chosenSubjects = new BehaviorSubject<CieChosenSubjectGrade[]>([new CieChosenSubjectGrade()]);
  chosenSubjects$ = new Observable<CieChosenSubjectGrade[]>();

  private readonly calculatePushed = new BehaviorSubject<boolean>(false);
  calculatePushed$: Observable<boolean>;

  private readonly subjectChanged = new BehaviorSubject<boolean>(false);
  subjectModified$: Observable<boolean>;

  constructor() {
    this.cieAllSubjects$ = this.cieAllSubjects.asObservable().pipe(shareReplay(1));
    this.calculatedTotalScore$ = this.calculatedTotalScore.asObservable().pipe(shareReplay(1));
    this.calculatePushed$ = this.calculatePushed.asObservable().pipe(shareReplay(1));
    this.chosenSubjects$ = this.chosenSubjects.asObservable().pipe(shareReplay(1));
    this.subjectModified$ = this.subjectChanged.asObservable().pipe(shareReplay(1));
  }

  initCie() {
    this.storageService.getItem<CieChosenSubjectGrade[]>('cieResults').then((grades) => {
      if (grades) {
        this.chosenSubjects.next(
          grades.map<CieChosenSubjectGrade>(grade => new CieChosenSubjectGrade(
                          new CieSubject(grade.subject.subjectCode, grade.subject.subjectName, grade.subject.testComponent, grade.subject.include),
                          new CieGrade(grade.grade.academicLevel, grade.grade.rankScore, grade.grade.result, grade.grade.subjectUnit),
                          grade.gradesList.map(grd => new CieGrade(grd.academicLevel, grd.rankScore, grd.result, grd.subjectUnit) )
                        ))
        );
      }
    });

    this.userService.loggedIn$
      .pipe(
        filter((loggedIn) => loggedIn === true),
        take(1)
      )
      .subscribe(_isLoggedIn =>
        this.getSavedSubjectsGrades()
          .pipe(
            take(1),
            map(({message: {cieGrade: grades}}) => grades),
            filter(grades => grades?.length > 0)
          )
          .subscribe(grades => {
              this.chosenSubjects.next(
                grades.map<CieChosenSubjectGrade>(grade => new CieChosenSubjectGrade(
                                new CieSubject(grade.subject.subjectCode, grade.subject.subjectName, grade.subject.testComponent, grade.subject.include),
                                new CieGrade(grade.grade.academicLevel, grade.grade.rankScore, grade.grade.result, grade.grade.subjectUnit),
                                grade.gradesList.map(grd => new CieGrade(grd.academicLevel, grd.rankScore, grd.result, grd.subjectUnit) )
                              ))
              );
          })
      );
  }

  getSubjects(): Observable<CieSubject[]> {
    this.http
      .get<ResponseDto<CieSubject[]>>(`${environment.apiUrl}subjects/cie`)
      .pipe(take(1))
      .subscribe((subject) => {
        this.cieAllSubjects.next(subject.message.map(s => new CieSubject(s.subjectCode, s.subjectName, s.testComponent, s.include)));
      });

      return this.cieAllSubjects$;
  }

  getGrades(subjectCode: string, testComponent: string): Observable<CieGrade[]> {
    return this.http
      .get<ResponseDto<CieSubjectGrades>>(`${environment.apiUrl}grades/fetch-grades?subjectCode=${subjectCode}&testComponent=${testComponent}`)
      .pipe(
        map((results) =>
          results.message.results.map((grade: CieGrade) => {
            const result = grade.result.includes('*') ? grade.result.substring(0, 1) + '+' : grade.result;
            return new CieGrade(grade.academicLevel, grade.rankScore, result, grade.subjectUnit);
          })
        )
      );
  }

  addNewRow(): void {
    this.chosenSubjects$.pipe(take(1)).subscribe((subjects) => {
      subjects.push(new CieChosenSubjectGrade());
      this.chosenSubjects.next(subjects);
    });
  }

  resetChosenSubjects(): void {
    this.chosenSubjects.next([new CieChosenSubjectGrade()]);

    this.gradesChanged();
    this.userService.loggedIn$
      .pipe(
        filter((loggedIn) => loggedIn === true),
        take(1)
      )
      .subscribe((_) => this.saveSubjectsGrades([]).pipe(take(1)).subscribe());

    this.storageService.removeItem('cieResults');
  }

  gradesChanged(): void {
    this.calculatePushed.next(false);
    this.calculatedTotalScore.next('');
  }

  subjectModified(): void {
    this.subjectChanged.next(true);
  }

  calculate(): void {
    this.chosenSubjects$.pipe(take(1)).subscribe((subjectList) => {
      const subjects = subjectList
        .filter((sub) => sub.subject.testComponent != '')
        .map(
          (sub) =>
            new CalculationRequestSubjects(
              sub.subject.testComponent,
              sub.subject.subjectCode,
              sub.grade.academicLevel,
              sub.grade.result.replace('+', '*')
            )
        );

      if (subjects.length > 0) {
        forkJoin({
          calculation: this.getCieCalculation(subjects).pipe(take(1)),
          chosenSubjects: this.chosenSubjects$.pipe(take(1)),
        }).subscribe(({ calculation, chosenSubjects }) => {
          this.calculatedTotalScore.next(calculation.message.totalScore);

          calculation.message.cieRankScore.forEach((respSubject) => {
            const found = chosenSubjects.findIndex((subject) => subject.subject.testComponent === respSubject.testComponent);

            if (found >= 0) {
              chosenSubjects[found].subject.include = respSubject.include;
            }
          });

          this.calculatePushed.next(true);
        });
      }
    });
  }

  getCieCalculation(requestSubjects: CalculationRequestSubjects[]): Observable<ResponseDto<CieRankScoreCalc>> {
    return this.http.post<ResponseDto<CieRankScoreCalc>>(`${environment.apiUrl}calculate/cie`, requestSubjects);
  }

  saveSubjectsGrades(grades: CieChosenSubjectGrade[]): Observable<ResponseDto> {
    return this.http.put<ResponseDto>(`${environment.apiUrl}grades/save`, { cieGrade: grades });
  }

  getSavedSubjectsGrades(): Observable<ResponseDto<StudentSavedGrades>> {
    return this.http.get<ResponseDto<StudentSavedGrades>>(`${environment.apiUrl}grades/save`);
  }
}
